/**
 * The period filter shared by the Modern records tabs and the stat cards.
 *
 * "month" has no tab of its own and is reachable only from the third stat card.
 */
export type RecordsPeriod = "all" | "today" | "week" | "month"

export type DateRange = {
    fromDate: string
    toDate: string
}

/**
 * `YYYY-MM-DD` for the `fromDate`/`toDate` query parameters.
 * @param date
 */
export function toApiDate(date: Date): string {
    const month = date.getMonth() + 1;
    const day = date.getDate();

    return `${date.getFullYear()}-${month < 10 ? "0" : ""}${month}-${day < 10 ? "0" : ""}${day}`
}

/**
 * The `fromDate`/`toDate` pair for a period, or `null` for "all".
 * @param period
 * @param now reference date, injected so this is testable
 */
export function periodRange(period: RecordsPeriod, now: Date): DateRange | null {
    const year = now.getFullYear();
    const month = now.getMonth();
    const day = now.getDate();

    if (period === "today") {
        const today = toApiDate(now);

        return {fromDate: today, toDate: today}
    }

    if (period === "week") {
        // getDay() is 0 for Sunday; (dow + 6) % 7 turns that into "days since
        // Monday", so Sunday counts as the END of its week rather than the start
        // of the next one.
        const sinceMonday = (now.getDay() + 6) % 7;

        return {
            fromDate: toApiDate(new Date(year, month, day - sinceMonday)),
            toDate: toApiDate(new Date(year, month, day - sinceMonday + 6)),
        }
    }

    if (period === "month") {
        // Day 0 of the NEXT month is the last day of this one.
        return {
            fromDate: toApiDate(new Date(year, month, 1)),
            toDate: toApiDate(new Date(year, month + 1, 0)),
        }
    }

    return null
}

/**
 * The full query for `GET /v2/record/telemedicine/doctor/{id}/open`.
 *
 * @param period
 * @param page
 * @param size
 * @param now reference date for the range, injected so this is testable
 */
export function buildRecordsSearch(
  period: RecordsPeriod,
  page: number,
  size: number,
  now: Date,
): {[key: string]: number | string} {
    const range = periodRange(period, now);
    const search: {[key: string]: number | string} = {size, page};

    if (range !== null) {
        search.fromDate = range.fromDate;
        search.toDate = range.toDate
    }

    return search
}
